import { ChangeEvent, FocusEvent } from 'react';
import {
  FieldValues,
  Path,
  UseFormReturn,
  ControllerFieldState,
} from 'react-hook-form';
import { FieldUIStates } from './Form.config';
import {
  FieldErrorAlertFn,
  FieldStateData,
  InitialFieldStateFn,
  TransformError,
} from './Form.types';

export const fieldStateReducer = (
  fieldState: ControllerFieldState,
  isFieldErrorAlert: FieldErrorAlertFn = defaultIsFieldErrorAlert,
  transformError: TransformError = defaultTransformError,
  initialFieldState: InitialFieldStateFn = defaultFieldState,
): FieldStateData => {
  const output = initialFieldState();
  const { error, isTouched } = fieldState;
  const errorMessage = error?.message;

  if (!(typeof errorMessage === 'string' && isTouched)) {
    return output;
  }
  const fieldStateOutput = isFieldErrorAlert(errorMessage);
  output.fieldState = fieldStateOutput;
  output.messageProps = transformError(errorMessage);

  return output;
};

function defaultIsFieldErrorAlert(error: string) {
  if (error === 'error.required') return FieldUIStates.alert;
  return FieldUIStates.error;
}

function defaultTransformError(errorMessage: string) {
  return { i18nKey: errorMessage };
}

function defaultFieldState() {
  return {
    fieldState: FieldUIStates.idle,
    messageProps: null,
  };
}

//wrapper per injectare i metodi di useForm
export function buildAutocompleteHandlerCreator<
  F extends FieldValues,
  T extends
    | FocusEvent<HTMLInputElement>
    | ChangeEvent<HTMLInputElement> = ChangeEvent<HTMLInputElement>,
>(methods: UseFormReturn<F>) {
  /**
   * Questa funzione viene usata per creare una callback da passare all'onChange di un input
   * sostanzialmente se l'onChange è strato triggerato dall'autofill ( o comunque da una fonte esterna )
   * (document.activeElement !== event?.target)
   */
  return function createHandleAutocompleteOnChangeCallback(
    fn?: (event: T) => any,
  ) {
    return function handleOnChangeCallback(event: T) {
      const isActiveElement = document.activeElement === event?.target;
      //se è l'elemento attivo non facciamo nulla ( perchè significa che siamo nell'input )
      if (isActiveElement) return event;
      //vogliamo assicurarci di 2 cose
      const name = event.target.name as Path<F>;
      //1) che il campo sia abbia lo stato di touched
      const isTouched = methods.getFieldState(name).isTouched;
      //Se il campo non è touched non viene mostrato l'eventuale errore ( quindi lo forziamo a touched (se non lo è già))
      if (!isTouched) {
        methods.setValue(name, methods.getValues(name), {
          shouldTouch: true,
          shouldDirty: true,
        });
      }
      //2)Eseguiamo un'eventuale callback se presente ( per esempio per il campo email, se viene autocompletato dal browser
      //vogliamo far partire la verifyEmail per assicurarci che venga validata )
      if (typeof fn === 'function') fn(event);

      return event;
    };
  };
}
